import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { FieldType } from '@sb-tools/typescript/enums/field-type.enum';
import { FieldsService } from 'src/app/core/services/ticket/fields.service';
import { TicketFormattedDataService } from 'src/app/core/services/ticket/ticket-formatted-data.service';
import { DatePipe } from '@angular/common';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { SignalRService } from 'src/app/core/services/signalR.service';
import { FieldsLogService } from 'src/app/core/services/fields-log.service';
import { TooltipDirective, TooltipModule } from 'ngx-bootstrap/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TicketTabType } from '../../core/enums/ticket-tab-type.enum';
import { TicketRightPanelService } from '../../core/services/ticket-right-panel.service';
import { DetailedTicketDTO } from '../../core/models/dto/detailed-ticket.dto';
import { TicketFieldsUpdatedEventModel } from '../../core/models/dto/signalR/ticket-fields-updated-event-model.model';
import { RouterLink } from '@angular/router';
import { ClipboardModule } from 'ngx-clipboard';
import { InlineSVGComponent } from '../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { EncodedFieldParamPipe } from '../../shared/pipes/encoded-field-param.pipe';

@Component({
    selector: 'app-fields',
    templateUrl: './fields.component.html',
    styleUrls: ['./fields.component.scss'],
    imports: [TooltipModule, RouterLink, ClipboardModule, InlineSVGComponent, TranslateModule, EncodedFieldParamPipe],
})
export class FieldsComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() ticket: DetailedTicketDTO;

    clipboardData;
    copyFieldsTooltip: string;
    isAddressData: boolean;

    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        private fieldsService: FieldsService,
        private ticketFormattedDataService: TicketFormattedDataService,
        private signalRService: SignalRService,
        private ticketRightPanelService: TicketRightPanelService,
        private fieldsLogService: FieldsLogService,
        private translateService: TranslateService,
    ) {}

    ngOnInit() {
        this.isAddressData = this.ticketFormattedDataService.checkIsAddressData(this.ticket.communityName);
        this.copyFieldsTooltip = this.isAddressData
            ? this.translateService.instant('Click to copy address to the clipboard')
            : this.translateService.instant('Click to copy fields to the clipboard');

        this.processFields();
        this.setupSignalRListeners();

        this.fieldsService.fieldsUpdated.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
            this.processFields();
        });
    }

    ngAfterViewInit() {
        this.fieldsLogService.checkCustomFieldEqual(this.ticket.id, this.ticket.source);
    }

    openEditFieldsTab(): void {
        this.ticketRightPanelService.open({
            ticket: this.ticket,
            tabType: TicketTabType.editFields,
            isFocusNeeded: true,
        });
    }

    processFields(): void {
        (this.ticket.fields || []).forEach((field) => {
            if (field.type === FieldType.date) {
                field.transformedValue = this.transformToLocalDate(field.value);
            }
        });

        if (!this.isAddressData) {
            this.clipboardData = this.ticketFormattedDataService.getFieldsData(this.ticket);
            return;
        }
        this.clipboardData = this.ticketFormattedDataService.getAddressData(this.ticket);
    }

    setIsCopied(customFieldsTooltip: TooltipDirective): void {
        this.copyFieldsTooltip = this.translateService.instant('Copied');
        customFieldsTooltip.hide();
        setTimeout(() => {
            customFieldsTooltip.show();
        }, 0);
        setTimeout(() => {
            this.copyFieldsTooltip = this.isAddressData
                ? this.translateService.instant('Click to copy address to the clipboard')
                : this.translateService.instant('Click to copy fields to the clipboard');
        }, 1000);
    }

    transformToLocalDate(date): string {
        if (!date) {
            return '';
        }
        const isDateWithTime = date.length > 10;
        return isDateWithTime
            ? new DatePipe('en').transform(date, 'dd.MM.yyyy HH:mm')
            : new DatePipe('en').transform(date, 'dd.MM.yyyy HH:mm').substring(0, 10);
    }

    private setupSignalRListeners(): void {
        this.signalRService
            .onTicketFieldsUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: TicketFieldsUpdatedEventModel) => {
                if (this.ticket?.id === data.ticketId) {
                    this.ticket.fields = data.fields;
                }
                this.processFields();
            });
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
